// src/iTwin/export/ifc/psets/drainageUtilitiesPset.ts
import type { DrainageUtilities } from "../../../../domain/utilities/DrainageUtilities";
import type { UtilityData } from "../../../../domain/utilities/UtilityData";
import type { UtilityFootprint } from "../../../../domain/utilities/UtilityFootprint";
import type { UtilityQuality } from "../../../../domain/utilities/UtilityQuality";
import type { NextId } from "../core/step";
import {
  ifcBoolean,
  ifcReal,
  ifcText,
  writePropertySetForElement,
  type IfcProp,
} from "../core/step";

function asTrimmedString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function asNumber(v: unknown): number | undefined {
  if (v === null || v === undefined) return undefined;
  const n = typeof v === "number" ? v : Number(String(v));
  return Number.isFinite(n) ? n : undefined;
}

function asBoolean(v: unknown): boolean | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;

  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "t" || s === "1" || s === "yes" || s === "y") return true;
  if (s === "false" || s === "f" || s === "0" || s === "no" || s === "n") return false;

  return undefined;
}

function addText(props: IfcProp[], name: string, v: unknown): void {
  const s = asTrimmedString(v);
  if (!s) return;
  props.push({ name, value: ifcText(s) });
}

function addNumber(props: IfcProp[], name: string, v: unknown): void {
  const raw = asTrimmedString(v);
  if (!raw) return;
  const n = asNumber(raw);
  if (n === undefined) {
    addText(props, name, raw);
    return;
  }
  props.push({ name, value: ifcReal(n) });
}

function addBool(props: IfcProp[], name: string, v: unknown): void {
  const b = asBoolean(v);
  if (b === undefined) return;
  props.push({ name, value: ifcBoolean(b) });
}

function hasAnyDrainageData(d?: DrainageUtilities): boolean {
  if (!d) return false;
  const rec = d as unknown as { [key: string]: unknown };
  for (const key in rec) {
    if (!Object.prototype.hasOwnProperty.call(d, key)) continue;
    const v = rec[key];
    const s = asTrimmedString(v);
    if (s.length > 0) return true;
  }
  return false;
}

function hasAnyUtilityData(u?: UtilityData): boolean {
  if (!u) return false;
  const rec = u as unknown as { [key: string]: unknown };
  for (const key in rec) {
    if (!Object.prototype.hasOwnProperty.call(u, key)) continue;
    const s = asTrimmedString(rec[key]);
    if (s.length > 0) return true;
  }
  return false;
}

function hasAnyUtilityFootprint(f?: UtilityFootprint): boolean {
  if (!f) return false;
  const len = asNumber((f as any).LengthMeters);
  const wid = asNumber((f as any).WidthMeters);
  return (len !== undefined && len !== 0) || (wid !== undefined && wid !== 0);
}

function hasAnyUtilityQuality(q?: UtilityQuality): boolean {
  if (!q) return false;
  const rec = q as unknown as { [key: string]: unknown };
  for (const key in rec) {
    if (!Object.prototype.hasOwnProperty.call(q, key)) continue;
    const s = asTrimmedString(rec[key]);
    if (s.length > 0) return true;
  }
  return false;
}

function buildUtilityGeneralProps(d: DrainageUtilities): IfcProp[] {
  const props: IfcProp[] = [];

  addText(props, "CIF ID", d.CIFID);
  addText(props, "Element", d.Element);
  addText(props, "Element Type", d.ElementType);
  addText(props, "Network Type", d.NetworkType);
  addText(props, "User Defined Element Type", d.UserDefinedElementType);
  addText(props, "User Defined Network Type", d.UserDefinedNetworkType);
  addText(props, "EC Class ID", d.ECClassID);
  addText(props, "EC Instance ID", d.ECInstanceID);
  addBool(props, "Is Active", d.IsActive);

  return props;
}

function buildUtilityPhysicalProps(d: DrainageUtilities): IfcProp[] {
  const props: IfcProp[] = [];

  // Booleans
  addBool(props, "Elevation Is Invert", d.ElevationIsInvert);
  addBool(props, "Independent Conduit Elevations", d.IndependentConduitElevations);
  addBool(props, "Match Slope Of Conduit", d.MatchSlopeOfConduit);
  addBool(props, "Orient Top To Surface", d.OrientTopToSurface);
  addBool(props, "Only Include Contributing Slopes", d.PROPOnlyIncludeContributingSlopes);
  addBool(props, "Use Longitudinal Slope", d.UseLongitudinalSlope);
  addBool(props, "Use Road Slope", d.UseRoadSlope);

  // Numeric-ish fields
  addNumber(props, "Baseline Offset", d.BaselineOffset);
  addNumber(props, "Baseline Station", d.BaselineStation);
  addNumber(props, "Bypassed Flow", d.BypassedFlow);
  addNumber(props, "Captured Flow", d.CapturedFlow);
  addNumber(props, "Curb Opening Length", d.CurbOpeningLength);
  addNumber(props, "Depth", d.Depth);
  addNumber(props, "Diameter", d.Diameter);
  addNumber(props, "Grate Length", d.GrateLength);
  addNumber(props, "Grate Width", d.GrateWidth);
  addNumber(props, "Ground Elevation", d.GroundElevation);
  addNumber(props, "Invert Elevation", d.InvertElevation);
  addNumber(props, "Length", d.Length);
  addNumber(props, "Longitudinal Slope", d.LongitudinalSlope);
  addNumber(props, "Rim Elevation", d.RimElevation);
  addNumber(props, "Road Cross Slope Distance", d.RoadCrossSlopeDistance);
  addNumber(props, "Set Out Elevation", d.SetOutElevation);
  addNumber(props, "Set Out X", d.SetOutX);
  addNumber(props, "Set Out Y", d.SetOutY);
  addNumber(props, "Spread", d.Spread);
  addNumber(props, "Width", d.Width);

  return props;
}

function buildUtilityReferencesProps(d: DrainageUtilities): IfcProp[] {
  const props: IfcProp[] = [];

  addText(props, "Active Reference", d.ActiveReference);
  addText(props, "Active Station Offset Reference", d.ActiveStationOffsetReference);
  addText(props, "Delineation Reference", d.DelineationReference);
  addText(props, "Owner", d.PROPOwner);
  addText(props, "Crosssection Offset", d.PROPPROPCrosssectionOffset);

  return props;
}

function buildUtilityDataProps(u: UtilityData): IfcProp[] {
  const props: IfcProp[] = [];

  addText(props, "Element Type User Defined", u.ElementTypeUserDefined);
  addText(props, "Network Type", u.NetworkType);
  addText(props, "Network Type User Defined", u.NetworkTypeUserDefined);
  addText(props, "Node Type", u.NodeType);
  addText(props, "Operational Status", u.OperationalStatus);
  addText(props, "Owner", u.Owner);

  return props;
}

function buildUtilityFootprintProps(f: UtilityFootprint): IfcProp[] {
  const props: IfcProp[] = [];

  addNumber(props, "Length Meters", f.LengthMeters);
  addNumber(props, "Width Meters", f.WidthMeters);

  return props;
}

function buildUtilityQualityProps(q: UtilityQuality): IfcProp[] {
  const props: IfcProp[] = [];

  addText(props, "Quality Level", q.QualityLevel);
  addText(props, "Investigation Level", q.InvestigationLevel);
  addText(props, "Authority", q.Authority);
  addText(props, "Authority Description", q.AuthorityDescription);

  return props;
}

export function writeDrainageUtilitiesPset(args: {
  getNextId: NextId;
  ownerHistoryId: number;
  elementId: number;
  drainageUtilities?: DrainageUtilities;
  utilityData?: UtilityData;
  utilityFootprint?: UtilityFootprint;
  utilityQuality?: UtilityQuality;
}): string {
  const {
    getNextId,
    ownerHistoryId,
    elementId,
    drainageUtilities,
    utilityData,
    utilityFootprint,
    utilityQuality,
  } = args;

  let out = "";

  if (hasAnyDrainageData(drainageUtilities)) {
    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "<General> [Utilities]",
      props: buildUtilityGeneralProps(drainageUtilities!),
    });

    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "Physical [Utilities]",
      props: buildUtilityPhysicalProps(drainageUtilities!),
    });

    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "References [Utilities]",
      props: buildUtilityReferencesProps(drainageUtilities!),
    });
  }

  if (hasAnyUtilityData(utilityData)) {
    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "Utility Data [Utilities]",
      props: buildUtilityDataProps(utilityData!),
    });
  }

  if (hasAnyUtilityFootprint(utilityFootprint)) {
    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "Utility Footprint [Utilities]",
      props: buildUtilityFootprintProps(utilityFootprint!),
    });
  }

  if (hasAnyUtilityQuality(utilityQuality)) {
    out += writePropertySetForElement({
      getNextId,
      ownerHistoryId,
      elementId,
      psetName: "Utility Quality [Utilities]",
      props: buildUtilityQualityProps(utilityQuality!),
    });
  }

  return out;
}
